import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  toggleReaction, getUserReaction, recordReadTime,
  getResponses, createResponse, deleteResponse, updateResponse,
  updateCapsuleContent, uploadImage, uploadMultipleImages,
  getResponseReplies, toggleResponseReaction, getUserResponseReaction,
  deleteCapsule
} from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { applyFormat, renderFormattedText } from '../lib/textFormat'
import FollowButton from './FollowButton'
import ImageGallery from './ImageGallery'
import './CapsuleCard.css'

const MAX_IMAGE_BYTES = 8 * 1024 * 1024
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

// Helper: normalize image data to handle both old image_url and new image_urls
function getImageUrls(data) {
  if (!data) return null
  // New format: image_urls array
  if (data.image_urls) return data.image_urls
  // Old format: single image_url (for backward compatibility)
  if (data.image_url) return data.image_url
  return null
}

// Shared hook for image attachment in composers
function useImageAttachment() {
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [imageError, setImageError] = useState('')
  const fileInputRef = useRef(null)

  const handleSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError('Only JPG, PNG, GIF, WebP images are supported.')
      e.target.value = ''
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError('Image must be under 8 MB.')
      e.target.value = ''
      return
    }
    setImageError('')
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    e.target.value = ''
  }

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setImageFile(null)
    setImagePreview(null)
    setImageError('')
  }

  const reset = () => removeImage()

  return { imageFile, imagePreview, imageError, fileInputRef, handleSelect, removeImage, reset }
}

const WORD_WPM = 200

function getReadTime(text) {
  const words = text.trim().split(/\s+/).length
  const minutes = Math.ceil(words / WORD_WPM)
  return minutes < 1 ? '< 1 min' : `~${minutes} min`
}

function getDepthLabel(score) {
  if (score >= 80) return { label: 'High depth', cls: 'depth-high' }
  if (score >= 50) return { label: 'Mid depth', cls: 'depth-mid' }
  if (score >= 20) return { label: 'Growing', cls: 'depth-low' }
  return null
}

function getCapsuleDepthTier(score) {
  if (!score || score <= 0) return null
  if (score >= 80) return { cls: 'cdepth-high' }
  if (score >= 50) return { cls: 'cdepth-mid' }
  if (score >= 20) return { cls: 'cdepth-low' }
  return null
}

function getInitials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}

function getPseudonym(profile = {}) {
  if (profile.username) return `@${profile.username}`
  if (profile.display_name) return profile.display_name
  return 'Member'
}

export default function CapsuleCard({ capsule, autoOpenResponses = false }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [liked, setLiked] = useState(false)
  const [disliked, setDisliked] = useState(false)
  const [likeCount, setLikeCount] = useState(capsule.reaction_count || 0)
  const [dislikeCount, setDislikeCount] = useState(capsule.dislike_count || 0)
  const [liking, setLiking] = useState(false)
  const [disliking, setDisliking] = useState(false)
  const [responseCount, setResponseCount] = useState(capsule.response_count || 0)
  const readStartRef = useRef(null)
  const cardRef = useRef(null)

  // Responses panel
  const [showResponses, setShowResponses] = useState(false)
  const [responses, setResponses] = useState([])
  const [loadingResponses, setLoadingResponses] = useState(false)
  const [autoOpenedResponses, setAutoOpenedResponses] = useState(false)

  // Inline reply composer
  const [showComposer, setShowComposer] = useState(false)
  const [replyContent, setReplyContent] = useState('')
  const [replyLoading, setReplyLoading] = useState(false)
  const [replyError, setReplyError] = useState('')
  const [replySources, setReplySources] = useState([])
  const [replySourceUrl, setReplySourceUrl] = useState('')
  const [showReplySourceInput, setShowReplySourceInput] = useState(false)
  const replyImage = useImageAttachment()
  const replyTextareaRef = useRef(null)

  // Update Thoughts
  const [currentContent, setCurrentContent] = useState(capsule.content)
  const [iterations, setIterations] = useState(capsule.iterations || [])
  const [showUpdateEditor, setShowUpdateEditor] = useState(false)
  const [updateContent, setUpdateContent] = useState(capsule.content)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [updateError, setUpdateError] = useState('')
  const [showIterations, setShowIterations] = useState(false)

  const profile = capsule.profiles || {}
  const depth = getDepthLabel(profile.depth_score)
  const capsuleDepthTier = getCapsuleDepthTier(capsule.depth_feed_score)
  const isAuthor = user?.id === capsule.author_id

  // Track read time via IntersectionObserver
  useEffect(() => {
    if (!user || !capsule.id) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          readStartRef.current = Date.now()
        } else if (readStartRef.current) {
          const seconds = Math.floor((Date.now() - readStartRef.current) / 1000)
          readStartRef.current = null
          if (seconds > 4) recordReadTime(user.id, capsule.id, seconds)
        }
      },
      { threshold: 0.5 }
    )
    if (cardRef.current) observer.observe(cardRef.current)
    return () => observer.disconnect()
  }, [user, capsule.id])

  // Fetch the current user's existing reaction on mount
  useEffect(() => {
    if (!user) return
    getUserReaction(user.id, capsule.id).then(type => {
      if (type === 'like') setLiked(true)
      if (type === 'dislike') setDisliked(true)
    })
  }, [user, capsule.id])

  useEffect(() => {
    if (!autoOpenResponses || autoOpenedResponses || showResponses) return
    setAutoOpenedResponses(true)
    setShowResponses(true)
    setLoadingResponses(true)
    getResponses(capsule.id, user?.id)
      .then(data => setResponses(data))
      .catch(console.error)
      .finally(() => setLoadingResponses(false))
  }, [autoOpenResponses, autoOpenedResponses, showResponses, capsule.id, user?.id])

  const handleClickProfile = () => {
    if (profile.username) {
      navigate(`/user/${profile.username}`)
    }
  }

  const handleLike = async () => {
    if (!user || liking || disliking) return
    setLiking(true)
    try {
      const result = await toggleReaction(user.id, capsule.id, 'like')
      if (result.action === 'added') {
        setLiked(true)
        setLikeCount(c => c + 1)
      } else if (result.action === 'removed') {
        setLiked(false)
        setLikeCount(c => Math.max(0, c - 1))
      } else if (result.action === 'switched') {
        // was disliked, now liked
        setLiked(true)
        setDisliked(false)
        setLikeCount(c => c + 1)
        setDislikeCount(c => Math.max(0, c - 1))
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLiking(false)
    }
  }

  const handleDislike = async () => {
    if (!user || liking || disliking) return
    setDisliking(true)
    try {
      const result = await toggleReaction(user.id, capsule.id, 'dislike')
      if (result.action === 'added') {
        setDisliked(true)
        setDislikeCount(c => c + 1)
      } else if (result.action === 'removed') {
        setDisliked(false)
        setDislikeCount(c => Math.max(0, c - 1))
      } else if (result.action === 'switched') {
        // was liked, now disliked
        setDisliked(true)
        setLiked(false)
        setDislikeCount(c => c + 1)
        setLikeCount(c => Math.max(0, c - 1))
      }
    } catch (e) {
      console.error(e)
    } finally {
      setDisliking(false)
    }
  }

  const handleInviteDebate = () => {
    if (!capsule.id) return
    const debateUrl = `${window.location.origin}/capsule/${capsule.id}`
    const message = `Join me in this debate on Agora: ${debateUrl}`

    navigator.clipboard.writeText(message).then(() => {
      alert('Debate invite copied to clipboard!')
    }).catch(() => {
      // Fallback if clipboard API fails
      alert('Copy this to share: ' + message)
    })
  }

  const handleDelete = async () => {
    if (!user || capsule.author_id !== user.id) return
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) return

    try {
      await deleteCapsule(capsule.id)
      // Post deleted successfully - parent component should handle removal
      if (onDelete) onDelete(capsule.id)
    } catch (e) {
      console.error('Delete failed:', e)
      alert(`Failed to delete post: ${e.message}`)
    }
  }

  const toggleResponses = async () => {
    if (!showResponses) {
      setShowResponses(true)
      setLoadingResponses(true)
      try {
        const data = await getResponses(capsule.id, user?.id)
        setResponses(data)
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingResponses(false)
      }
    } else {
      setShowResponses(false)
      setShowComposer(false)
    }
  }

  const addReplySource = () => {
    if (!replySourceUrl.trim()) {
      setReplyError('Please enter a URL')
      return
    }

    try {
      new URL(replySourceUrl.trim())
    } catch (e) {
      setReplyError('Invalid URL format')
      return
    }

    const url = new URL(replySourceUrl.trim())
    const title = url.hostname.replace('www.', '')

    setReplySources(prev => [...prev, { title, url: replySourceUrl.trim() }])
    setReplySourceUrl('')
    setReplyError('')
  }

  const removeReplySource = (index) => {
    setReplySources(prev => prev.filter((_, i) => i !== index))
  }

  const submitReply = async () => {
    if (!replyContent.trim() || replyContent.trim().length < 20) return
    setReplyLoading(true)
    setReplyError('')
    try {
      let imageUrl = null
      if (replyImage.imageFile) imageUrl = await uploadImage(replyImage.imageFile, user.id)
      const sourcesData = replySources.length > 0 ? replySources : null
      const newResponse = await createResponse(user.id, capsule.id, replyContent.trim(), null, imageUrl, sourcesData)
      setResponses(prev => [...prev, newResponse])
      setResponseCount(c => c + 1)
      setReplyContent('')
      setReplySources([])
      setReplySourceUrl('')
      replyImage.reset()
      setShowComposer(false)
    } catch (e) {
      setReplyError(e.message)
    } finally {
      setReplyLoading(false)
    }
  }

  const handleDeleteResponse = async (responseId) => {
    try {
      await deleteResponse(responseId, user.id)
      setResponses(prev => prev.filter(r => r.id !== responseId))
      setResponseCount(c => Math.max(0, c - 1))
    } catch (e) {
      console.error(e)
    }
  }

  const handleUpdateResponse = async (responseId, newContent) => {
    try {
      const updated = await updateResponse(responseId, user.id, newContent)
      setResponses(prev => prev.map(r => r.id === responseId ? updated : r))
    } catch (e) {
      console.error(e)
    }
  }

  const handleUpdateThoughts = async () => {
    if (!updateContent.trim() || updateContent.trim() === currentContent.trim()) return
    setUpdateLoading(true)
    setUpdateError('')
    try {
      const updated = await updateCapsuleContent(capsule.id, user.id, updateContent.trim())
      setIterations(updated.iterations || [])
      setCurrentContent(updated.content)
      setShowUpdateEditor(false)
    } catch (e) {
      setUpdateError(e.message || 'Failed to update')
    } finally {
      setUpdateLoading(false)
    }
  }

  const openUpdateEditor = () => {
    setUpdateContent(currentContent)
    setUpdateError('')
    setShowUpdateEditor(true)
  }

  const timeAgo = capsule.publishes_at
    ? formatDistanceToNow(new Date(capsule.publishes_at), { addSuffix: true })
    : ''

  const totalVersions = iterations.length + 1

  return (
    <div className="capsule-card animate-slideUp" ref={cardRef}>
      {/* Header */}
      <div className="capsule-card-header">
        <button
          onClick={handleClickProfile}
          style={{ background: profile.avatar_color || '#1D4ED8' }}
          className="capsule-avatar capsule-avatar-btn"
          title={`View ${profile.display_name || 'user'}'s profile`}
        >
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="capsule-avatar-image" />
          ) : (
            getInitials(profile.display_name)
          )}
        </button>
        <div className="capsule-meta">
          <button
            onClick={handleClickProfile}
            className="capsule-author-btn"
            title={`View ${profile.display_name || 'user'}'s profile`}
          >
            <div className="capsule-author">
              {getPseudonym(profile)}
              {capsule.capsule_type === 'debate' && (
                <span className="capsule-type-badge debate">Debate</span>
              )}
            </div>
          </button>
          <div className="capsule-info">
            <button
              onClick={handleClickProfile}
              className="capsule-username-btn"
              title={`View @${profile.username}'s profile`}
            >
              <span className="mono" style={{ color: 'var(--text-dim)' }}>@{profile.username}</span>
            </button>
            <span className="capsule-dot">·</span>
            <span className="mono" style={{ color: 'var(--text-dim)' }}>{timeAgo}</span>
            {profile.display_name && profile.username && profile.display_name.toLowerCase() !== profile.username.toLowerCase() && (
              <>
                <span className="capsule-dot">·</span>
                <span className="mono" style={{ color: 'var(--text-dim)' }}>{profile.display_name}</span>
              </>
            )}
            {iterations.length > 0 && (
              <span className="capsule-updated-badge">updated</span>
            )}
          </div>
        </div>
        <FollowButton
          className="capsule-follow-btn"
          currentUserId={user?.id}
          targetUserId={capsule.author_id}
          variant="compact"
        />
        {depth && !showUpdateEditor && (
          <span className={`depth-badge ${depth.cls}`}>{depth.label}</span>
        )}
        {isAuthor && !showUpdateEditor && (
          <button className="capsule-update-btn" onClick={openUpdateEditor}>
            <PenIcon /> Update Thoughts
          </button>
        )}
        {isAuthor && showUpdateEditor && (
          <span className="capsule-editing-label">Editing…</span>
        )}
      </div>

      {/* Body — or Update Editor */}
      {showUpdateEditor ? (
        <div className="capsule-update-editor">
          <textarea
            className="capsule-update-input"
            value={updateContent}
            onChange={e => setUpdateContent(e.target.value)}
            maxLength={10000}
            rows={6}
            autoFocus
          />
          {updateError && (
            <div className="capsule-update-error">{updateError}</div>
          )}
          <div className="capsule-update-actions">
            <span className="mono capsule-update-meta">
              {updateContent.length}/10000
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-ghost"
                onClick={() => { setShowUpdateEditor(false); setUpdateError('') }}
                disabled={updateLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateThoughts}
                disabled={
                  updateLoading ||
                  updateContent.trim() === currentContent.trim() ||
                  updateContent.trim().length < 20
                }
              >
                {updateLoading ? <span className="spinner" /> : 'Seal Update →'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="capsule-body">
          {renderFormattedText(currentContent)}
          <ImageGallery images={getImageUrls(capsule)} />

          {/* Sources */}
          {capsule.sources && capsule.sources.length > 0 && (
            <div className="capsule-sources">
              <span className="capsule-sources-label">Sources:</span>
              {capsule.sources.map((source, idx) => (
                <a
                  key={idx}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="capsule-source-link"
                  title={source.url}
                >
                  {source.title} →
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {capsule.tags && capsule.tags.length > 0 && !showUpdateEditor && (
        <div className="capsule-tags">
          {capsule.tags.map(tag => (
            <span key={tag} className="tag">{tag}</span>
          ))}
        </div>
      )}

      {/* Footer */}
      {!showUpdateEditor && (
        <div className="capsule-footer">
          <button
            className={`capsule-action ${liked ? 'liked' : ''}`}
            onClick={handleLike}
            disabled={!user || liking || disliking}
          >
            <HeartIcon filled={liked} />
            <span>{likeCount}</span>
          </button>

          <button
            className={`capsule-action ${disliked ? 'disliked' : ''}`}
            onClick={handleDislike}
            disabled={!user || liking || disliking}
          >
            <DislikeIcon filled={disliked} />
            <span>{dislikeCount}</span>
          </button>

          <button className={`capsule-action ${showResponses ? 'active' : ''}`} onClick={toggleResponses}>
            <ReplyIcon />
            <span>{responseCount} {responseCount === 1 ? 'response' : 'responses'}</span>
            <span className="response-chevron">{showResponses ? '▴' : '▾'}</span>
          </button>

          {capsule.capsule_type === 'debate' && (
            <button className="capsule-action capsule-invite-btn" onClick={handleInviteDebate} title="Invite others to this debate">
              📨 Invite
            </button>
          )}

          {iterations.length > 0 && (
            <button
              className={`capsule-action ${showIterations ? 'active' : ''}`}
              onClick={() => setShowIterations(v => !v)}
            >
              <HistoryIcon />
              <span>{iterations.length} {iterations.length === 1 ? 'revision' : 'revisions'}</span>
              <span className="response-chevron">{showIterations ? '▴' : '▾'}</span>
            </button>
          )}

          <span className="capsule-read-time">{getReadTime(currentContent)}</span>

          {Number.isFinite(capsule.depth_feed_score) && (
            <span className={`capsule-depth-score ${capsuleDepthTier?.cls || ''}`} title="Capsule depth score">
              ⬡ {Math.round(capsule.depth_feed_score)}
            </span>
          )}

          {(capsule.bridging_score || 0) > 0 && (
            <span className="capsule-bridge-score" title="Cross-tribe bridging score">
              ↔ {Math.round(capsule.bridging_score)}
            </span>
          )}

          {user && capsule.author_id === user.id && (
            <button
              className="capsule-action delete-btn"
              onClick={handleDelete}
              title="Delete this post"
            >
              🗑
            </button>
          )}
        </div>
      )}

      {/* Responses panel */}
      {showResponses && (
        <div className="responses-panel">
          {loadingResponses ? (
            <div className="responses-loading"><span className="spinner" /></div>
          ) : (
            <>
              {responses.length === 0 && !showComposer && (
                <div className="responses-empty">No responses yet.</div>
              )}

              {responses.map(r =>
                new Date(r.publishes_at) <= new Date()
                  ? <PublishedResponse key={r.id} response={r} currentUser={user} capsuleId={capsule.id} />
                  : <PendingResponse
                      key={r.id}
                      response={r}
                      onDelete={() => handleDeleteResponse(r.id)}
                      onUpdate={(content) => handleUpdateResponse(r.id, content)}
                    />
              )}

              {user && !showComposer && (
                <button className="responses-reply-btn" onClick={() => setShowComposer(true)}>
                  + Write a response
                </button>
              )}

              {showComposer && (
                <div className="response-composer">
                  <p className="response-composer-microcopy">
                    Responses incubate for 20 minutes before going live.
                  </p>
                  {replyImage.imagePreview && (
                    <div className="composer-image-preview-wrap">
                      <img src={replyImage.imagePreview} alt="Preview" className="composer-image-preview" />
                      <button type="button" className="composer-image-remove-btn" onClick={replyImage.removeImage}>×</button>
                    </div>
                  )}
                  <div className="format-toolbar">
                    <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); applyFormat(replyTextareaRef.current, '**', replyContent, setReplyContent) }} title="Bold"><strong>B</strong></button>
                    <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); applyFormat(replyTextareaRef.current, '*', replyContent, setReplyContent) }} title="Italic"><em>I</em></button>
                    <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); applyFormat(replyTextareaRef.current, '__', replyContent, setReplyContent) }} title="Underline"><u>U</u></button>
                    <span className="format-toolbar-hint">select text then click</span>
                  </div>
                  <textarea
                    ref={replyTextareaRef}
                    className="response-composer-input"
                    placeholder="Take your time…"
                    value={replyContent}
                    onChange={e => setReplyContent(e.target.value)}
                    maxLength={10000}
                    rows={4}
                    autoFocus
                  />

                  {/* Sources section for response */}
                  <div className="response-sources">
                    <button
                      type="button"
                      className="response-sources-toggle"
                      onClick={() => setShowReplySourceInput(!showReplySourceInput)}
                    >
                      🔗 {replySources.length > 0 ? `Sources (${replySources.length})` : 'Add Source'}
                    </button>

                    {showReplySourceInput && (
                      <div className="response-source-input-wrapper">
                        <div className="response-source-input">
                          <input
                            type="url"
                            value={replySourceUrl}
                            onChange={(e) => setReplySourceUrl(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addReplySource()}
                            placeholder="Paste URL (https://...)"
                            className="response-source-url-input"
                          />
                          <button
                            type="button"
                            className="response-source-add-btn"
                            onClick={addReplySource}
                            disabled={replyLoading || !replySourceUrl.trim()}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}

                    {replySources.length > 0 && (
                      <div className="response-sources-list">
                        {replySources.map((source, idx) => (
                          <div key={idx} className="response-source-item">
                            <a
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="response-source-link"
                              title={source.url}
                            >
                              {source.title}
                            </a>
                            <button
                              type="button"
                              className="response-source-remove-btn"
                              onClick={() => removeReplySource(idx)}
                              disabled={replyLoading}
                              title="Remove source"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {replyImage.imageError && <div className="response-composer-error">{replyImage.imageError}</div>}
                  {replyError && <div className="response-composer-error">{replyError}</div>}
                  <div className="response-composer-footer">
                    <div className="composer-footer-left">
                      <input
                        ref={replyImage.fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={replyImage.handleSelect}
                      />
                      <button
                        type="button"
                        className={`composer-image-btn ${replyImage.imageFile ? 'active' : ''}`}
                        onClick={() => replyImage.fileInputRef.current?.click()}
                        disabled={replyLoading}
                        title="Attach image"
                      >
                        <CameraIcon />
                      </button>
                      <span className="mono response-composer-meta">
                        {replyContent.length}/10000 · 20-min incubation
                      </span>
                    </div>
                    <div className="response-composer-actions">
                      <button
                        className="btn btn-ghost"
                        onClick={() => { setShowComposer(false); setReplyContent(''); setReplyError(''); replyImage.reset() }}
                        disabled={replyLoading}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={submitReply}
                        disabled={replyLoading || replyContent.trim().length < 20}
                      >
                        {replyLoading ? <span className="spinner" /> : 'Seal Response →'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Iterations panel (past versions) */}
      {showIterations && iterations.length > 0 && (
        <div className="iterations-panel">
          <div className="iterations-panel-label">Past versions · {totalVersions} total</div>

          {/* Current version */}
          <div className="iteration-item iteration-item--current">
            <div className="iteration-version-label">
              <span className="iteration-badge iteration-badge--current">current · v{totalVersions}</span>
            </div>
            <div className="iteration-body">
              {renderFormattedText(
                currentContent.split('\n').filter(Boolean).slice(0, 3).join('\n')
              )}
              {currentContent.split('\n').filter(Boolean).length > 3 && (
                <p className="iteration-truncated">…</p>
              )}
            </div>
          </div>

          {/* Past versions newest → oldest */}
          {[...iterations].reverse().map((it, i) => {
            const vNum = iterations.length - i
            const when = formatDistanceToNow(new Date(it.updated_at), { addSuffix: true })
            return (
              <div key={i} className="iteration-item">
                <div className="iteration-version-label">
                  <span className="iteration-badge">v{vNum}</span>
                  <span className="iteration-when mono">{when}</span>
                </div>
                <div className="iteration-body">
                  {renderFormattedText(
                    it.content.split('\n').filter(Boolean).slice(0, 3).join('\n')
                  )}
                  {it.content.split('\n').filter(Boolean).length > 3 && (
                    <p className="iteration-truncated">…</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Published response ────────────────────────────────────────────────────────

function PublishedResponse({ response, currentUser, capsuleId }) {
  const navigate = useNavigate()
  const profile = response.profiles || {}
  const timeAgo = response.publishes_at
    ? formatDistanceToNow(new Date(response.publishes_at), { addSuffix: true })
    : ''

  const [liked, setLiked] = useState(false)
  const [disliked, setDisliked] = useState(false)
  const [likeCount, setLikeCount] = useState(response.like_count || 0)
  const [dislikeCount, setDislikeCount] = useState(response.dislike_count || 0)
  const [reacting, setReacting] = useState(false)

  const [showReplies, setShowReplies] = useState(false)
  const [replies, setReplies] = useState([])
  const [loadingReplies, setLoadingReplies] = useState(false)
  const [replyCount, setReplyCount] = useState(response.reply_count || 0)
  const [showReplyComposer, setShowReplyComposer] = useState(false)
  const [replyContent, setReplyContent] = useState('')
  const [replyLoading, setReplyLoading] = useState(false)
  const nestedImage = useImageAttachment()
  const nestedTextareaRef = useRef(null)

  useEffect(() => {
    if (!currentUser) return
    getUserResponseReaction(currentUser.id, response.id).then(type => {
      if (type === 'like') setLiked(true)
      if (type === 'dislike') setDisliked(true)
    })
  }, [currentUser, response.id])

  const handleClickProfile = () => {
    if (profile.username) {
      navigate(`/user/${profile.username}`)
    }
  }

  const handleReact = async (type) => {
    if (!currentUser || reacting) return
    setReacting(true)
    try {
      const result = await toggleResponseReaction(currentUser.id, response.id, type)
      if (type === 'like') {
        if (result.action === 'added')    { setLiked(true);  setLikeCount(c => c + 1) }
        if (result.action === 'removed')  { setLiked(false); setLikeCount(c => Math.max(0, c - 1)) }
        if (result.action === 'switched') { setLiked(true); setDisliked(false); setLikeCount(c => c + 1); setDislikeCount(c => Math.max(0, c - 1)) }
      } else {
        if (result.action === 'added')    { setDisliked(true);  setDislikeCount(c => c + 1) }
        if (result.action === 'removed')  { setDisliked(false); setDislikeCount(c => Math.max(0, c - 1)) }
        if (result.action === 'switched') { setDisliked(true); setLiked(false); setDislikeCount(c => c + 1); setLikeCount(c => Math.max(0, c - 1)) }
      }
    } catch (e) { console.error(e) }
    finally { setReacting(false) }
  }

  const toggleReplies = async () => {
    if (showReplies) {
      setShowReplies(false)
      setShowReplyComposer(false)
      return
    }
    setShowReplies(true)
    setLoadingReplies(true)
    try {
      const data = await getResponseReplies(response.id, currentUser?.id)
      setReplies(data)
    } catch (e) { console.error(e) }
    finally { setLoadingReplies(false) }
  }

  const openReply = () => {
    setShowReplies(true)
    setShowReplyComposer(true)
    if (!showReplies) {
      setLoadingReplies(true)
      getResponseReplies(response.id, currentUser?.id)
        .then(data => setReplies(data))
        .catch(console.error)
        .finally(() => setLoadingReplies(false))
    }
  }

  const submitReply = async () => {
    if (!replyContent.trim() || replyContent.trim().length < 10) return
    setReplyLoading(true)
    try {
      let imageUrl = null
      if (nestedImage.imageFile) imageUrl = await uploadImage(nestedImage.imageFile, currentUser.id)
      const newReply = await createResponse(currentUser.id, capsuleId, replyContent.trim(), response.id, imageUrl)
      setReplies(prev => [...prev, newReply])
      setReplyCount(c => c + 1)
      setReplyContent('')
      nestedImage.reset()
      setShowReplyComposer(false)
    } catch (e) { console.error(e) }
    finally { setReplyLoading(false) }
  }

  const handleDeleteReply = async (replyId) => {
    try {
      await deleteResponse(replyId, currentUser.id)
      setReplies(prev => prev.filter(r => r.id !== replyId))
      setReplyCount(c => Math.max(0, c - 1))
    } catch (e) { console.error(e) }
  }

  const handleUpdateReply = async (replyId, newContent) => {
    try {
      const updated = await updateResponse(replyId, currentUser.id, newContent)
      setReplies(prev => prev.map(r => r.id === replyId ? updated : r))
    } catch (e) { console.error(e) }
  }

  return (
    <div className="response-item">
      <div className="response-item-header">
        <button
          onClick={handleClickProfile}
          style={{ background: profile.avatar_color || '#1D4ED8' }}
          className="response-avatar response-avatar-btn"
          title={`View ${profile.display_name || 'user'}'s profile`}
        >
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="response-avatar-image" />
          ) : (
            getInitials(profile.display_name)
          )}
        </button>
        <div className="response-item-meta">
          <button
            onClick={handleClickProfile}
            className="response-author-btn"
            title={`View ${profile.display_name || 'user'}'s profile`}
          >
            <span className="response-author">{getPseudonym(profile)}</span>
          </button>
          <span className="mono response-time">{timeAgo}</span>
        </div>
        <FollowButton
          className="response-follow-btn"
          currentUserId={currentUser?.id}
          targetUserId={response.author_id}
          variant="compact"
        />
      </div>

      <div className="response-body">
        {renderFormattedText(response.content)}
        <ImageGallery images={getImageUrls(response)} compact={true} />

        {/* Response sources */}
        {response.sources && response.sources.length > 0 && (
          <div className="response-sources-display">
            <span className="response-sources-label">Sources:</span>
            {response.sources.map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="response-source-link-display"
                title={source.url}
              >
                {source.title} →
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Reaction + reply footer */}
      <div className="response-footer">
        <button className={`response-react-btn ${liked ? 'liked' : ''}`} onClick={() => handleReact('like')} disabled={!currentUser || reacting}>
          <HeartIcon filled={liked} /> {likeCount}
        </button>
        <button className={`response-react-btn ${disliked ? 'disliked' : ''}`} onClick={() => handleReact('dislike')} disabled={!currentUser || reacting}>
          <DislikeIcon filled={disliked} /> {dislikeCount}
        </button>
        {currentUser && (
          <button className="response-react-btn" onClick={openReply}>↩ Reply</button>
        )}
        {replyCount > 0 && (
          <button className={`response-react-btn ${showReplies ? 'active' : ''}`} onClick={toggleReplies}>
            {replyCount} {replyCount === 1 ? 'reply' : 'replies'} {showReplies ? '▴' : '▾'}
          </button>
        )}
      </div>

      {/* Nested replies */}
      {showReplies && (
        <div className="response-replies">
          {loadingReplies ? (
            <div className="responses-loading"><span className="spinner" /></div>
          ) : (
            replies.map(r =>
              new Date(r.publishes_at) <= new Date()
                ? <NestedReply key={r.id} reply={r} currentUser={currentUser} />
                : <PendingResponse
                    key={r.id}
                    response={r}
                    onDelete={() => handleDeleteReply(r.id)}
                    onUpdate={(content) => handleUpdateReply(r.id, content)}
                    nested
                  />
            )
          )}

          {showReplyComposer && (
            <div className="response-composer response-composer--nested">
              {nestedImage.imagePreview && (
                <div className="composer-image-preview-wrap">
                  <img src={nestedImage.imagePreview} alt="Preview" className="composer-image-preview" />
                  <button type="button" className="composer-image-remove-btn" onClick={nestedImage.removeImage}>×</button>
                </div>
              )}
              <div className="format-toolbar">
                <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); applyFormat(nestedTextareaRef.current, '**', replyContent, setReplyContent) }} title="Bold"><strong>B</strong></button>
                <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); applyFormat(nestedTextareaRef.current, '*', replyContent, setReplyContent) }} title="Italic"><em>I</em></button>
                <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); applyFormat(nestedTextareaRef.current, '__', replyContent, setReplyContent) }} title="Underline"><u>U</u></button>
                <span className="format-toolbar-hint">select text then click</span>
              </div>
              <textarea
                ref={nestedTextareaRef}
                className="response-composer-input"
                placeholder="Write a reply…"
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                maxLength={5000}
                rows={3}
                autoFocus
              />
              {nestedImage.imageError && <div className="response-composer-error">{nestedImage.imageError}</div>}
              <div className="response-composer-footer">
                <div className="composer-footer-left">
                  <input
                    ref={nestedImage.fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={nestedImage.handleSelect}
                  />
                  <button
                    type="button"
                    className={`composer-image-btn ${nestedImage.imageFile ? 'active' : ''}`}
                    onClick={() => nestedImage.fileInputRef.current?.click()}
                    disabled={replyLoading}
                    title="Attach image"
                  >
                    <CameraIcon />
                  </button>
                  <span className="mono response-composer-meta">{replyContent.length}/5000</span>
                </div>
                <div className="response-composer-actions">
                  <button className="btn btn-ghost" onClick={() => { setShowReplyComposer(false); setReplyContent(''); nestedImage.reset() }} disabled={replyLoading}>Cancel</button>
                  <button className="btn btn-primary" onClick={submitReply} disabled={replyLoading || replyContent.trim().length < 10}>
                    {replyLoading ? <span className="spinner" /> : 'Reply →'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentUser && !showReplyComposer && (
            <button className="responses-reply-btn" onClick={() => setShowReplyComposer(true)}>
              + Reply to this response
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Nested reply (one level deep, no further nesting) ─────────────────────────

function NestedReply({ reply, currentUser }) {
  const navigate = useNavigate()
  const profile = reply.profiles || {}
  const timeAgo = reply.publishes_at
    ? formatDistanceToNow(new Date(reply.publishes_at), { addSuffix: true })
    : ''

  const [liked, setLiked] = useState(false)
  const [disliked, setDisliked] = useState(false)
  const [likeCount, setLikeCount] = useState(reply.like_count || 0)
  const [dislikeCount, setDislikeCount] = useState(reply.dislike_count || 0)
  const [reacting, setReacting] = useState(false)

  useEffect(() => {
    if (!currentUser) return
    getUserResponseReaction(currentUser.id, reply.id).then(type => {
      if (type === 'like') setLiked(true)
      if (type === 'dislike') setDisliked(true)
    })
  }, [currentUser, reply.id])

  const handleClickProfile = () => {
    if (profile.username) {
      navigate(`/user/${profile.username}`)
    }
  }

  const handleReact = async (type) => {
    if (!currentUser || reacting) return
    setReacting(true)
    try {
      const result = await toggleResponseReaction(currentUser.id, reply.id, type)
      if (type === 'like') {
        if (result.action === 'added')    { setLiked(true);  setLikeCount(c => c + 1) }
        if (result.action === 'removed')  { setLiked(false); setLikeCount(c => Math.max(0, c - 1)) }
        if (result.action === 'switched') { setLiked(true); setDisliked(false); setLikeCount(c => c + 1); setDislikeCount(c => Math.max(0, c - 1)) }
      } else {
        if (result.action === 'added')    { setDisliked(true);  setDislikeCount(c => c + 1) }
        if (result.action === 'removed')  { setDisliked(false); setDislikeCount(c => Math.max(0, c - 1)) }
        if (result.action === 'switched') { setDisliked(true); setLiked(false); setDislikeCount(c => c + 1); setLikeCount(c => Math.max(0, c - 1)) }
      }
    } catch (e) { console.error(e) }
    finally { setReacting(false) }
  }

  return (
    <div className="nested-reply">
      <div className="response-item-header">
        <button
          onClick={handleClickProfile}
          style={{ background: profile.avatar_color || '#1D4ED8' }}
          className="response-avatar response-avatar--sm response-avatar-btn"
          title={`View ${profile.display_name || 'user'}'s profile`}
        >
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="response-avatar-image" />
          ) : (
            getInitials(profile.display_name)
          )}
        </button>
        <div className="response-item-meta">
          <button
            onClick={handleClickProfile}
            className="response-author-btn"
            title={`View ${profile.display_name || 'user'}'s profile`}
          >
            <span className="response-author">{getPseudonym(profile)}</span>
          </button>
          <span className="mono response-time">{timeAgo}</span>
        </div>
        <FollowButton
          className="response-follow-btn"
          currentUserId={currentUser?.id}
          targetUserId={reply.author_id}
          variant="compact"
        />
      </div>
      <div className="response-body response-body--nested">
        {renderFormattedText(reply.content)}
        <ImageGallery images={getImageUrls(reply)} compact={true} />

        {/* Nested reply sources */}
        {reply.sources && reply.sources.length > 0 && (
          <div className="response-sources-display">
            <span className="response-sources-label">Sources:</span>
            {reply.sources.map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="response-source-link-display"
                title={source.url}
              >
                {source.title} →
              </a>
            ))}
          </div>
        )}
      </div>
      <div className="response-footer">
        <button className={`response-react-btn ${liked ? 'liked' : ''}`} onClick={() => handleReact('like')} disabled={!currentUser || reacting}>
          <HeartIcon filled={liked} /> {likeCount}
        </button>
        <button className={`response-react-btn ${disliked ? 'disliked' : ''}`} onClick={() => handleReact('dislike')} disabled={!currentUser || reacting}>
          <DislikeIcon filled={disliked} /> {dislikeCount}
        </button>
      </div>
    </div>
  )
}

// ── Pending response (user's own, in incubation) ──────────────────────────────

function PendingResponse({ response, onDelete, onUpdate }) {
  const [editing, setEditing] = useState(false)
  const [editContent, setEditContent] = useState(response.content)
  const [saving, setSaving] = useState(false)
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    const update = () => {
      const diff = new Date(response.publishes_at) - new Date()
      if (diff <= 0) { setTimeLeft('Publishing…'); return }
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`)
    }
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [response.publishes_at])

  const handleSave = async () => {
    if (!editContent.trim() || editContent.trim().length < 20) return
    setSaving(true)
    try {
      await onUpdate(editContent.trim())
      setEditing(false)
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="response-item response-item--pending">
      <div className="response-incubation-badge">
        <div className="incubation-dot" />
        In incubation — publishes in <span className="incubation-countdown">{timeLeft}</span>
      </div>

      {editing ? (
        <div className="response-edit">
          <textarea
            className="response-edit-input"
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            maxLength={10000}
            rows={4}
            autoFocus
          />
          <div className="response-edit-actions">
            <button
              className="btn btn-ghost"
              onClick={() => { setEditing(false); setEditContent(response.content) }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || editContent.trim().length < 20}
            >
              {saving ? <span className="spinner" /> : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="response-body">
            {renderFormattedText(response.content)}
          </div>
          <div className="response-pending-actions">
            <button className="response-action-btn" onClick={() => setEditing(true)}>Edit</button>
            <button className="response-action-btn response-action-btn--delete" onClick={onDelete}>Delete</button>
          </div>
        </>
      )}
    </div>
  )
}

// ── Pending capsule card ──────────────────────────────────────────────────────

export function PendingCapsuleCard({ capsule, onWithdraw, onOpenWorkspace, onOpenThread, publicView = false }) {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    const update = () => {
      const diff = new Date(capsule.publishes_at) - new Date()
      if (diff <= 0) { setTimeLeft('Publishing…'); return }
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`)
    }
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [capsule.publishes_at])

  return (
    <div className={`capsule-card capsule-card--incubating ${publicView ? 'capsule-card--public-incubating' : ''} animate-slideUp`}>
      <div className="incubation-badge">
        <div className="incubation-dot" />
        Incubating — publishes in{' '}
        <span className="incubation-countdown">{timeLeft}</span>
      </div>

      <div className="capsule-body">
        {renderFormattedText(capsule.content)}
        <ImageGallery images={getImageUrls(capsule)} />
      </div>

      <div className="capsule-footer">
        {publicView ? (
          <>
            <button className="btn btn-ghost" onClick={() => onOpenThread?.()}>
              Open thread
            </button>
            <span className="mono" style={{ color: 'var(--text-dim)', marginLeft: '0.75rem' }}>
              Visible during incubation
            </span>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={() => onOpenWorkspace?.()}>
              Open workspace
            </button>
            <button className="btn btn-danger" onClick={() => onWithdraw?.(capsule)}>
              Withdraw capsule
            </button>
            <span className="mono" style={{ color: 'var(--text-dim)', marginLeft: '0.75rem' }}>
              30-min incubation
            </span>
          </>
        )}
      </div>
    </div>
  )
}

// ── Icons ─────────────────────────────────────────────────────────────────────

const HeartIcon = ({ filled }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
)

const ReplyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
)

const DislikeIcon = ({ filled }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
    <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
  </svg>
)

const PenIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const HistoryIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 .49-4.5" />
    <line x1="12" y1="7" x2="12" y2="12" />
    <line x1="12" y1="12" x2="15" y2="14" />
  </svg>
)

const CameraIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
)
